import { useMemo, useState } from 'react';
import type { ReactNode } from 'react';
import { cx } from './utils';

export type DataTableSortDirection = 'asc' | 'desc';
export type DataTableSortValue = string | number | boolean | null | undefined;

export type DataTableColumn<Row> = {
  key: string;
  header: ReactNode;
  align?: 'start' | 'center' | 'end';
  width?: string;
  sortable?: boolean;
  sortValue?: (row: Row) => DataTableSortValue;
  render: (row: Row) => ReactNode;
};

export interface DataTableSortState {
  key: string;
  direction: DataTableSortDirection;
}

export interface DataTableProps<Row> {
  columns: Array<DataTableColumn<Row>>;
  rows: Row[];
  getRowKey: (row: Row, index: number) => string;
  caption?: ReactNode;
  emptyState?: ReactNode;
  density?: 'comfortable' | 'compact';
  sortable?: boolean;
  initialSort?: DataTableSortState;
  className?: string;
}

function defaultSortValue<Row>(column: DataTableColumn<Row>, row: Row): DataTableSortValue {
  if (column.sortValue) return column.sortValue(row);
  const rendered = column.render(row);
  if (
    typeof rendered === 'string'
    || typeof rendered === 'number'
    || typeof rendered === 'boolean'
  ) {
    return rendered;
  }
  return '';
}

function compareSortValues(a: DataTableSortValue, b: DataTableSortValue) {
  if (typeof a === 'number' && typeof b === 'number') return a - b;
  if (typeof a === 'boolean' && typeof b === 'boolean') return Number(a) - Number(b);
  return String(a ?? '').localeCompare(String(b ?? ''), undefined, {
    numeric: true,
    sensitivity: 'base',
  });
}

export function DataTable<Row>({
  columns,
  rows,
  getRowKey,
  caption,
  emptyState,
  density = 'comfortable',
  sortable = true,
  initialSort,
  className,
}: DataTableProps<Row>) {
  const [sortState, setSortState] = useState<DataTableSortState | null>(initialSort ?? null);

  const sortedRows = useMemo(() => {
    if (!sortable || !sortState) return rows;
    const column = columns.find((item) => item.key === sortState.key);
    if (!column || column.sortable === false) return rows;

    return [...rows].sort((left, right) => {
      const result = compareSortValues(
        defaultSortValue(column, left),
        defaultSortValue(column, right),
      );
      return sortState.direction === 'asc' ? result : -result;
    });
  }, [columns, rows, sortState, sortable]);

  const handleSort = (column: DataTableColumn<Row>) => {
    if (!sortable || column.sortable === false) return;
    setSortState((current) => {
      if (current?.key !== column.key) return { key: column.key, direction: 'asc' };
      return { key: column.key, direction: current.direction === 'asc' ? 'desc' : 'asc' };
    });
  };

  return (
    <div className={cx('ui-data-table', `ui-data-table--${density}`, className)}>
      <table>
        {caption ? <caption>{caption}</caption> : null}
        <thead>
          <tr>
            {columns.map((column) => {
              const isSortable = sortable && column.sortable !== false;
              const direction = sortState?.key === column.key ? sortState.direction : null;

              return (
                <th
                  key={column.key}
                  style={column.width ? { width: column.width } : undefined}
                  data-align={column.align}
                  aria-sort={direction ? (direction === 'asc' ? 'ascending' : 'descending') : 'none'}
                >
                  {isSortable ? (
                    <button
                      className="ui-data-table__sort-button"
                      type="button"
                      data-align={column.align}
                      onClick={() => handleSort(column)}
                    >
                      <span className="ui-data-table__header-label">{column.header}</span>
                      <span className="ui-data-table__sort-indicator" aria-hidden="true">
                        {direction === 'asc' ? '▲' : direction === 'desc' ? '▼' : '↕'}
                      </span>
                    </button>
                  ) : (
                    column.header
                  )}
                </th>
              );
            })}
          </tr>
        </thead>
        <tbody>
          {sortedRows.length > 0 ? (
            sortedRows.map((row, index) => (
              <tr key={getRowKey(row, index)}>
                {columns.map((column) => (
                  <td key={column.key} data-align={column.align}>
                    {column.render(row)}
                  </td>
                ))}
              </tr>
            ))
          ) : (
            <tr>
              <td colSpan={columns.length} className="ui-data-table__empty">
                {emptyState}
              </td>
            </tr>
          )}
        </tbody>
      </table>
    </div>
  );
}
